import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './LeadMagnetLandingPage.css';
import { leadMagnetApi } from '../../services/apis/configuredApis';
import { getEnvVar } from '../../utils/env';
import StandardLandingTemplate from './StandardLandingTemplate';
import StorytellingLandingTemplate from './StorytellingLandingTemplate';

// Helper function to construct download URL
// In dev (no REACT_APP_API_URL): uses http://localhost:5000
// In prod (or with REACT_APP_API_URL): uses runtime-injected env var, or relative URL
export const constructDownloadUrl = (assetId) => {
  if (!assetId) return '#';

  let baseUrl;
  const apiUrl = getEnvVar('REACT_APP_API_URL');

  if (apiUrl && apiUrl !== '/api') {
    baseUrl = apiUrl;
  } else if (process.env.NODE_ENV === 'development') {
    baseUrl = 'http://localhost:5000';
  } else {
    baseUrl = '';
  }

  const apiPath = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  return `${apiPath}/assets/${assetId}/download`;
};

/**
 * Transform API response to component format. Both templates receive the same
 * shape; storytelling-specific fields (soundBytes, funnelLinks, ctaBox) are
 * passed through untouched.
 */
const transformLeadMagnetData = (apiData) => {
  return {
    leadMagnetId: apiData.id,
    slug: apiData.slug,
    pageType: apiData.page_type || 'STANDARD',
    pageTitle: apiData.page_title,
    mainHeadline: apiData.hero_headline,
    subHeadline: apiData.hero_subheadline,
    benefits: apiData.bullet_points || [],
    credibilityText: apiData.credibility_text,
    seoTitle: apiData.seo_title,
    seoDescription: apiData.seo_description,
    formConfig: {
      jobTitleRequired: false,
      ctaButtonText: apiData.cta_text || 'Download Now',
      downloadUrl: constructDownloadUrl(apiData.asset_id),
      emailLink: true,
      thankYouMessage: 'Thank you for your interest!',
      thankYouExtraText: 'Your download should start automatically. Check your inbox for a copy.',
    },
    soundBytes: apiData.sound_bytes || {},
    funnelLinks: apiData.funnel_links || [],
    ctaBox: apiData.cta_box || {},
    isPublished: apiData.status === 'PUBLISHED',
  };
};

function LeadMagnetLandingPage() {
  const { slug } = useParams();
  const [leadMagnet, setLeadMagnet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLeadMagnet = async () => {
      if (!slug) {
        setLeadMagnet(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await leadMagnetApi.leadMagnetListGet({
          search: slug,
          status: 'PUBLISHED',
          createdBy: null,
          assetId: null,
          limit: 100,
          offset: 0,
        });

        const responseData = response?.data || response;
        const leadMagnetsList = responseData?.leadMagnets || [];

        const matchingLeadMagnet = leadMagnetsList.find(
          (lm) => lm.slug === slug && lm.status === 'PUBLISHED'
        );

        if (matchingLeadMagnet) {
          setLeadMagnet(transformLeadMagnetData(matchingLeadMagnet));
        } else {
          setLeadMagnet(null);
        }
      } catch (error) {
        console.error('Error loading lead magnet:', error);
        setLeadMagnet(null);
      } finally {
        setLoading(false);
      }
    };

    loadLeadMagnet();
  }, [slug]);

  if (loading) {
    return (
      <div className="lead-magnet-page">
        <div className="lead-magnet-container">
          <div className="loading-state">Loading...</div>
        </div>
      </div>
    );
  }

  if (!leadMagnet) {
    return (
      <div className="lead-magnet-page">
        <div className="lead-magnet-container">
          <div className="error-state">
            <h1>404</h1>
            <p>Lead magnet not found</p>
          </div>
        </div>
      </div>
    );
  }

  if (leadMagnet.pageType === 'STORYTELLING') {
    return <StorytellingLandingTemplate leadMagnet={leadMagnet} />;
  }

  return <StandardLandingTemplate leadMagnet={leadMagnet} />;
}

export default LeadMagnetLandingPage;
